import get from 'lodash.get';
import JsonApi from 'utils/jsonApi';
import { urlComposer } from 'utils/string';
import type {
  ArticleObject,
  CreateArticlePayload,
  GetUserArticleRequest,
  GetUserArticlesRequest,
  GetUserArticlesResponse,
  UpdateArticlePayload,
  ArticleMedia,
  ArticleBodyPayload,
} from 'commons/types';

export default class UserArticle {
  jsonApi: JsonApi;

  constructor(jsonApi: JsonApi) {
    this.jsonApi = jsonApi;
  }

  async getUserArticles({
    id,
    articleType,
    page = 1,
    imageWidth,
    isUGC = true,
  }: GetUserArticlesRequest): Promise<GetUserArticlesResponse> {
    const statusFilter = `{"name":"article_status","op":"eq","val":"${encodeURIComponent(
      articleType,
    )}"}`;
    const ugcFilter = '{"name": "user_generated_content","op": "eq", "val":"true"}';
    const filter = isUGC ? `[${statusFilter}, ${ugcFilter}]` : `[${statusFilter}]`;
    const urlString = urlComposer(`/user/${id}/articles`, {
      filter,
      imageWidth,
      page,
    });
    const userArticleResponse = await this.jsonApi.get(urlString);
    const userArticle = get(userArticleResponse, `entities.userArticle.${id}`, {});
    return userArticle;
  }

  async getUserArticle({
    articleId,
    userId,
  }: GetUserArticleRequest): Promise<GetUserArticlesResponse> {
    const urlString = `/user/${userId}/articles/${articleId}`;
    const userArticleResponse = await this.jsonApi.get(urlString);
    const camelCaseArticleId =
      typeof articleId[0] === 'string'
        ? articleId[0].toLowerCase() + articleId.slice(1)
        : articleId;
    const userArticle = get(userArticleResponse, `entities.article.${camelCaseArticleId}`, {});
    return userArticle;
  }

  static buildArticleBody(
    title: string,
    subtitle: string,
    content: string,
    media: ArticleMedia,
    submittedForPublication: boolean,
    articleId?: string,
  ): ArticleBodyPayload {
    return {
      type: 'article',
      id: articleId,
      attributes: {
        title,
        subtitle,
        content,
        media,
        submittedForPublication: submittedForPublication ? 'true' : 'false',
      },
    };
  }

  async createArticle({
    title,
    subtitle,
    content,
    userId,
    media,
    submittedForPublication = false,
  }: CreateArticlePayload): Promise<ArticleObject> {
    const payload = UserArticle.buildArticleBody(
      title,
      subtitle,
      content,
      media,
      submittedForPublication,
    );

    if (payload.attributes.media === null) {
      delete payload.attributes.media;
    }

    const url = `/user/${userId}/articles/`;
    const createArticleResponse = await this.jsonApi.post(url, payload, false);
    const article = get(createArticleResponse, 'entities.article', {});
    return Object.values(article)[0] as any;
  }

  async updateArticle({
    title,
    subtitle,
    content,
    media,
    userId,
    submittedForPublication = false,
    articleId,
  }: UpdateArticlePayload): Promise<ArticleObject> {
    const payload = UserArticle.buildArticleBody(
      title,
      subtitle,
      content,
      media,
      submittedForPublication,
      articleId,
    );

    if (payload.attributes.media === null) {
      delete payload.attributes.media;
    }

    const url = `/user/${userId}/articles/${articleId}`;
    const updateArticleResponse = await this.jsonApi.patch(url, payload);
    const camelCaseArticleId = articleId.charAt(0).toLowerCase() + articleId.slice(1);
    return get(updateArticleResponse, `entities.article.${camelCaseArticleId}`, {});
  }
}
